import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { SnapshotSharedVariablesService } from 'src/app/modules/snapshot/Services/snapshot-shared-variables.service';
import { SnapshotService } from 'src/app/modules/snapshot/Services/snapshot.service';
import { CustomsnapshotService } from 'src/app/modules/snapshot/Services/customsnapshot.service';
import { LoaderService } from '../../services/loader.service';

export interface CategoryNavTile {
  snapshotName: string;
  category: string;
  bucket: string;
  displayLabel: string;
  navSnapshotType: string;
  navSection: string;
  navSubSection: string;
  sort_id: number;
}

export interface CategoryNavGroup {
  items: CategoryNavTile[];
  sort_id: number;
}

@Component({
  selector: 'app-header-nav-snap-output',
  templateUrl: './header-nav-snap-output.component.html',
  styleUrls: ['./header-nav-snap-output.component.css'],
})
export class HeaderNavSnapOutputComponent {
  @Input() isAddClientDetails: boolean = false;
  @Input() snapNameNavHeader: string = '';
  @Input() mainStudyName: string = '';
  @Input() ImageURL: string = '';

  @Output() defaultPanelChange = new EventEmitter<boolean>();
  clientSnapDetails: any;
  snapshotListData: any;
  selected_Snap: string = '';
  AcrossDropdown: any[] = [];
  snapshotType_name: any = '';

  categoryNavGroups: CategoryNavGroup[] = [];
  useCategoryNavDropdown = false;
  activeCategory = '';
  private snapshotConfigList: any[] = [];
  private landingPageTilesData: any[] = [];

  private readonly bucketDisplayOrder = ['All Business Lines', 'Business Line Specific'];
  private readonly snapshotTypeSortOrder = [
    'Single Brand',
    'Multiple Brands',
    'Multiple Categories',
    'Multiple Markets',
    'Audience Snapshot',
  ];

  constructor(
    private router: Router,
    private _loaderService: LoaderService,
    public _configService: SnapshotService,
    private clientDetails: SnapshotSharedVariablesService,
    private _configCustomService: CustomsnapshotService,
  ) {
    this.ImageURL = '';
  }
  ngOnInit(): void {
    this.clientSnapDetails = this.clientDetails.clientDetails;
    this.snapshotListData = this._configService.SnapshotList;
    this.snapshotType_name = sessionStorage.getItem('snapshotType');
    this.selected_Snap = this.clientSnapDetails?.study ?? '';
    if (this._configService.snapshotTypeSelectedName) {
      this._configService.snapshotTypeSelectedName = this.snapshotType_name;
    }

    if (this.getClientCode()) {
      this.loadCategoryNavDropdown();
    }
  }

  get categoryLabel(): string {
    const tile = this.findActiveCategoryTile();
    if (tile?.category?.trim()) {
      return this._configService.capitalizeWords(tile.category.trim());
    }
    const category =
      this.activeCategory ||
      this.resolveCategoryForCurrentStudy(this.snapshotConfigList) ||
      '';
    if (category) {
      return this._configService.capitalizeWords(category);
    }
    return this._configService.capitalizeWords(this.snapNameNavHeader || '');
  }

  /** Section label shown in header (e.g. Brand Health). */
  get activeSectionLabel(): string {
    const tile = this.findActiveCategoryTile();
    if (tile?.displayLabel) {
      return this._configService.capitalizeWords(tile.displayLabel);
    }
    if (
      this._configService.selectedSubSection &&
      this._configService.selectedSubSection !== '' &&
      this._configService.selectedSubSection !== 'NA'
    ) {
      return this._configService.capitalizeWords(
        this._configService.selectedSubSection,
      );
    }
    return this._configService.capitalizeWords(
      this._configService.selectedSection || '',
    );
  }

  get hasLegacyAcrossDropdown(): boolean {
    return Array.isArray(this.AcrossDropdown) && this.AcrossDropdown.length > 0;
  }

  showSnapshotDropdownVal: boolean = false;

  showSnapshotDropdown(): void {
    this.showSnapshotDropdownVal = !this.showSnapshotDropdownVal;
    if (this.showSnapshotDropdownVal) {
      this.loadCategoryNavDropdown();
    }
  }

  private getClientCode(): string {
    return (
      this.clientSnapDetails?.client_code ||
      this.clientSnapDetails?.client_name ||
      ''
    );
  }

  private getCurrentSnapshotCategory(): string {
    return (
      this._configService.outputSnapshotData?.category?.trim() ||
      this._configService.alreadySavedSnapshotData?.category?.trim() ||
      ''
    );
  }

  private resolveCategoryForCurrentStudy(snapshots?: any[]): string {
    const studyName = (this.clientSnapDetails?.study || '').trim();
    if (!studyName) {
      return this.getCurrentSnapshotCategory();
    }

    const clientCode = this.getClientCode();
    const fromServiceCache = this._configService.getSnapshotByName(
      studyName,
      clientCode,
    );
    if (fromServiceCache?.category?.trim()) {
      return fromServiceCache.category.trim();
    }

    const list = snapshots ?? this.snapshotConfigList;
    const match = list?.find(
      (snap: any) =>
        (snap.snapshot_name || '').trim().toLowerCase() ===
        studyName.toLowerCase(),
    );
    if (match?.category?.trim()) {
      return match.category.trim();
    }

    return this.getCurrentSnapshotCategory();
  }

  private normalizeBucket(bucket: string): string {
    return (bucket || '').trim().toLowerCase();
  }

  private normalizeTile(s: string): string {
    return s.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
  }

  private getTileSortId(category: string, bucketName: string, sectionName: string): number {
    if (!this.landingPageTilesData?.length) return 999;
    const n = this.normalizeTile.bind(this);
    const study = this.landingPageTilesData.find(
      (s: any) => n(s.study_name) === n(category)
    );
    if (!study?.headers) return 999;
    const header = study.headers.find(
      (h: any) => n(h.header_name) === n(bucketName)
    );
    if (!header?.tiles) return 999;
    const tile = header.tiles.find(
      (t: any) => n(t.section_name) === n(sectionName)
    );
    return tile?.sort_id ?? 999;
  }

  private getBucketSortId(category: string, bucketName: string): number {
    if (!this.landingPageTilesData?.length) return 999;
    const n = this.normalizeTile.bind(this);
    const study = this.landingPageTilesData.find(
      (s: any) => n(s.study_name) === n(category)
    );
    if (!study?.headers) return 999;
    const header = study.headers.find(
      (h: any) => n(h.header_name) === n(bucketName)
    );
    return header?.sort_id ?? 999;
  }

  /**
   * Sort snapshots by the tile order defined in landingPageTilesData:
   * primary sort by bucket sort_id, secondary by minimum tile sort_id within the snapshot.
   */
  private sortSnapshotsByTileOrder(snapshots: any[], category: string): any[] {
    return [...snapshots].sort((a, b) => {
      const bucketA = this.getBucketSortId(category, a.bucket);
      const bucketB = this.getBucketSortId(category, b.bucket);
      if (bucketA !== bucketB) return bucketA - bucketB;
      // Within the same bucket, sort by snapshot_name as stable fallback
      return (a.snapshot_name || '').localeCompare(b.snapshot_name || '');
    });
  }

  private getUserAccessibleSnapshotNames(): string[] {
    const clients = this._configService.SnapshotList;
    if (!clients?.length) {
      return [];
    }
    const clientCode = this.getClientCode();
    const client = clients.find(
      (c: any) =>
        (c.ClientName || c.client_name || '').toLowerCase() ===
        clientCode.toLowerCase(),
    );
    if (!client?.UniqueSnapshots?.length) {
      return [];
    }
    return client.UniqueSnapshots.map((item: any) =>
      typeof item === 'string' ? item.trim() : (item?.SnapshotName || '').trim(),
    ).filter((name: string) => !!name);
  }

  private filterSnapshotsByUserAccess(snapshots: any[]): any[] {
    const accessible = this.getUserAccessibleSnapshotNames();
    if (!accessible.length) {
      return [];
    }
    const accessibleSet = new Set(
      accessible.map((n) => n.toLowerCase()),
    );
    return snapshots.filter((snap: any) =>
      accessibleSet.has((snap.snapshot_name || '').trim().toLowerCase()),
    );
  }

  private resolveSectionTiles(snapshotConfig: any): CategoryNavTile[] {
    const tiles: CategoryNavTile[] = [];
    const lib = this._configCustomService.getCustomSnapshotWidgetLibraryByName(
      snapshotConfig.snapshot_name,
      this.getClientCode(),
    );
    if (!lib?.snapshotType?.length) {
      return tiles;
    }

    const snapshotTypeSet = new Set(
      (snapshotConfig.snapshotType_details || []).map(
        (d: any) => d.snapshot_type,
      ),
    );

    let types = lib.snapshotType;
    if (snapshotTypeSet.size > 0) {
      types = types.filter((t: any) =>
        snapshotTypeSet.has(t.snapshotType_name),
      );
    }

    types.sort(
      (a: any, b: any) =>
        this.snapshotTypeSortOrder.indexOf(a.snapshotType_name) -
        this.snapshotTypeSortOrder.indexOf(b.snapshotType_name),
    );

    for (const snapshotType of types) {
      for (const section of snapshotType.section || []) {
        const subsections = section.subsection || [];
        if (
          subsections.length === 0 ||
          subsections[0]?.subsection_name === 'NA'
        ) {
          tiles.push({
            snapshotName: snapshotConfig.snapshot_name,
            category: snapshotConfig.category,
            bucket: snapshotConfig.bucket,
            displayLabel: section.section_name,
            navSnapshotType: snapshotType.snapshotType_name,
            navSection: section.section_name,
            navSubSection: '',
            sort_id: this.getTileSortId(snapshotConfig.category, snapshotConfig.bucket, section.section_name),
          });
        } else {
          for (const subsection of subsections) {
            const subName = subsection.subsection_name;
            const isNa = subName?.toLowerCase() === 'na';
            tiles.push({
              snapshotName: snapshotConfig.snapshot_name,
              category: snapshotConfig.category,
              bucket: snapshotConfig.bucket,
              displayLabel: isNa ? section.section_name : subName,
              navSnapshotType: snapshotType.snapshotType_name,
              navSection: section.section_name,
              navSubSection: isNa ? '' : subName,
              sort_id: this.getTileSortId(snapshotConfig.category, snapshotConfig.bucket, section.section_name),
            });
          }
        }
      }
    }

    return tiles;
  }

  private buildCategoryNavGroups(
    snapshotsInCategory: any[],
  ): CategoryNavGroup[] {
    const groups: CategoryNavGroup[] = [];

    for (const bucketName of this.bucketDisplayOrder) {
      const bucketNorm = this.normalizeBucket(bucketName);
      const items: CategoryNavTile[] = [];

      for (const snap of snapshotsInCategory) {
        if (this.normalizeBucket(snap.bucket) !== bucketNorm) {
          continue;
        }
        items.push(...this.resolveSectionTiles(snap));
      }

      if (items.length > 0) {
        // Sort tiles within bucket by sort_id from landingPageTilesData
        items.sort((a, b) => (a.sort_id ?? 999) - (b.sort_id ?? 999));
        const groupSortId = this.getBucketSortId(this.activeCategory, bucketName);
        groups.push({ items, sort_id: groupSortId });
      }
    }

    // Sort bucket groups by their sort_id from landingPageTilesData
    groups.sort((a, b) => (a.sort_id ?? 999) - (b.sort_id ?? 999));
    return groups;
  }

  private loadLegacyAcrossDropdown(): void {
    const stored = localStorage.getItem('AcrossDropdown');
    if (!stored) {
      this.AcrossDropdown = [];
      return;
    }
    try {
      this.AcrossDropdown = JSON.parse(stored);
      const sortOrder = [
        'Single Brand',
        'Multiple Brands',
        'Multiple Markets',
        'Audience Snapshot',
      ];
      this.AcrossDropdown.sort(
        (a: any, b: any) =>
          sortOrder.indexOf(a.snapshotType_name) -
          sortOrder.indexOf(b.snapshotType_name),
      );
    } catch {
      this.AcrossDropdown = [];
    }
  }

  loadCategoryNavDropdown(): void {
    const clientCode = this.getClientCode();

    if (!clientCode) {
      this.useCategoryNavDropdown = false;
      this.loadLegacyAcrossDropdown();
      return;
    }

    this._configService.getAllSnapshotData(clientCode).subscribe({
      next: (response: any) => {
        if (!response?.Data) {
          this.finishCategoryNavLoad([], true);
          return;
        }

        const allSnapshots = JSON.parse(response.Data).filter(
          (snap: any) =>
            snap?.snapshot_name != null && snap.snapshot_name.trim() !== '',
        );
        const accessible = this.filterSnapshotsByUserAccess(allSnapshots);
        this.snapshotConfigList = accessible;

        const category = this.resolveCategoryForCurrentStudy(accessible);
        if (!category) {
          this.finishCategoryNavLoad([], true);
          return;
        }

        this.activeCategory = category;

        const categoryNorm = category.trim().toLowerCase();
        const inCategory = accessible.filter(
          (snap: any) =>
            (snap.category || '').trim().toLowerCase() === categoryNorm,
        );

        if (inCategory.length === 0) {
          this.finishCategoryNavLoad([], true);
          return;
        }

        const snapshotNames = inCategory.map((s: any) => s.snapshot_name);
        forkJoin({
          libraries: this._configCustomService.getAllCustomSnapshotWidgetLibrary(clientCode, snapshotNames),
          tilesData: this._configService.getLandingPageTiles(),
        }).subscribe({
          next: ({ tilesData }) => {
            if (tilesData) {
              this.landingPageTilesData = tilesData;
            }
            // Sort snapshots by the tile order from landingPageTilesData before building groups
            const orderedSnapshots = this.sortSnapshotsByTileOrder(inCategory, category);
            const groups = this.buildCategoryNavGroups(orderedSnapshots);
            this.finishCategoryNavLoad(groups, groups.length === 0);
          },
          error: () => this.finishCategoryNavLoad([], true),
        });
      },
      error: () => this.finishCategoryNavLoad([], true),
    });
  }

  private finishCategoryNavLoad(
    groups: CategoryNavGroup[],
    useLegacy: boolean,
  ): void {
    this.categoryNavGroups = groups;
    this.useCategoryNavDropdown = groups.length > 0;
    if (useLegacy) {
      this.loadLegacyAcrossDropdown();
    }
  }

  findActiveCategoryTile(): CategoryNavTile | null {
    for (const group of this.categoryNavGroups) {
      for (const tile of group.items) {
        if (this.isCategoryTileActive(tile)) {
          return tile;
        }
      }
    }

    const studyName = (this.clientSnapDetails?.study || '').trim().toLowerCase();
    const config = this.snapshotConfigList.find(
      (snap: any) =>
        (snap.snapshot_name || '').trim().toLowerCase() === studyName,
    );
    if (config) {
      const tiles = this.resolveSectionTiles(config);
      return tiles.find((tile) => this.isCategoryTileActive(tile)) ?? null;
    }

    return null;
  }

  isCategoryTileActive(tile: CategoryNavTile): boolean {
    const currentSnap = (this.clientSnapDetails?.study || '')
      .trim()
      .toLowerCase();
    if (tile.snapshotName.trim().toLowerCase() !== currentSnap) {
      return false;
    }
    if (tile.navSubSection) {
      return (
        this._configService.selectedSection === tile.navSection &&
        this._configService.selectedSubSection === tile.navSubSection
      );
    }
    return (
      this._configService.selectedSection === tile.navSection &&
      (!this._configService.selectedSubSection ||
        this._configService.selectedSubSection === 'NA' ||
        this._configService.selectedSubSection === '')
    );
  }

  navigateCategoryTile(tile: CategoryNavTile): void {
    this.showSnapshotDropdownVal = false;

    const previousStudy = (this.clientSnapDetails?.study || '').trim();
    const forceReload =
      tile.snapshotName.trim().toLowerCase() !==
      previousStudy.toLowerCase();

    if (forceReload) {
      this.clientSnapDetails.study = tile.snapshotName;
      this.clientDetails.clientDetails.study = tile.snapshotName;
    }

    const navItem = { snapshotType_name: tile.navSnapshotType };
    const navSection = { section_name: tile.navSection };
    const navSubsection = {
      subsection_name: tile.navSubSection || 'NA',
    };
    this.navigateSnapshot(navItem, navSection, navSubsection, forceReload);
  }

  isLegacySectionActive(
    item: any,
    section: any,
    subsection?: any,
  ): boolean {
    if (subsection?.subsection_name) {
      return (
        this._configService.selectedSubSection !== '' &&
        subsection.subsection_name === this._configService.selectedSubSection &&
        this._configService.selectedSection === section.section_name
      );
    }
    return (
      this._configService.selectedSection !== '' &&
      section.section_name === this._configService.selectedSection &&
      this._configService.snapshotTypeSelectedName === item.snapshotType_name
    );
  }

  navigateSnapshot(
    item: any,
    section: any,
    subsection: any,
    forceReload: boolean = false,
  ) {
    const snapshotType = sessionStorage.getItem('snapshotType');
    sessionStorage.setItem('sectionName', section.section_name);
    const subSection = subsection?.subsection_name
      ? subsection.subsection_name
      : 'NA';
    sessionStorage.setItem('subsectionName', subSection);
    this.clientDetails.clientDetails.sectionselected = section.section_name;
    this.clientDetails.clientDetails.subsectionselected =
      subsection?.subsection_name;
    this.clientSnapDetails.sectionselected = section.section_name;
    this.clientSnapDetails.subsectionselected = subsection?.subsection_name;
    this._configService.selectedSection = section.section_name;
    this._configService.selectedSubSection = subsection?.subsection_name ?? '';
    this.snapshotType_name = item.snapshotType_name;
    this._configService.snapshotTypeSelectedName = item.snapshotType_name;

    if (!forceReload && snapshotType === item.snapshotType_name) {
      this._configService.tiggerSelectionSnapshotType = false;
      this._configService.isSameSnapshotType.next(true);
      this.defaultPanelChange.emit(true);
      return;
    }

    this._configService.tiggerSelectionSnapshotType = true;
    sessionStorage.removeItem('dataLoaded');
    sessionStorage.setItem('snapshotType', item.snapshotType_name);
    this._configService.isSameSnapshotType.next(false);

    this._loaderService.showLoader();
    this._configService
      .getClientStudyData({
        client_code: this.getClientCode(),
        snapshot_name: this.clientSnapDetails.study,
      })
      .subscribe(async (b) => {
        const x = JSON.parse(b.body.Data);
        if (x != null && x != undefined && x != '') {
          const mainStudy = x.study_list[0].study_name;

          if (x?.saveselection) {
            this._configService.globalDefaultSelectionFlag =
              x.saveselection.find(
                (item: { key: string; value: boolean }) =>
                  item.key === 'Global Default Selection',
              )?.value ?? false;
          }

          const data: {
            client_code: string;
            study: string;
            main_study: string;
            study_category: string;
            snapList: any;
            snapshotselected: string;
            sectionselected: any;
            subsectionselected: any;
            isAcrossdropDown: boolean;
          } = {
            client_code: this.getClientCode(),
            study: this.clientSnapDetails.study,
            main_study: mainStudy,
            study_category: x.category || '',
            snapList: this._configService.SnapshotList,
            snapshotselected: item.snapshotType_name,
            sectionselected: section.section_name,
            subsectionselected: subSection,
            isAcrossdropDown: true,
          };

          if (x.product == 'Standard') {
            this.router.navigateByUrl('Snapshot/snapshot-output', {
              state: data,
            });
          } else if (x.product == 'Custom') {
            this.router.navigateByUrl('/Snapshot', { skipLocationChange: true }).then(() => {
              this.router.navigateByUrl('Snapshot/custom-snapshot-output', {
                state: data,
              });
            });
          }
        } else {
          this.router.navigateByUrl('landing');
        }
      });
  }
}
